import {
  BrowserType,
  DeviceType,
  KameleoonLogger,
  OperatingSystemType,
  PageView,
} from '@kameleoon/javascript-sdk-core/browser';
import {
  BrowserResult,
  OSResult,
  PlatformAnalysisResult,
  PlatformResult,
} from 'src/utils/types';

export class Utils {
  static getFirstMatch(regex: RegExp, userAgent: string): string {
    const match = userAgent.match(regex);
    return (match && match.length > 0 && match[1]) || '';
  }

  static getSecondMatch(regex: RegExp, userAgent: string): string {
    const match = userAgent.match(regex);
    return (match && match.length > 1 && match[2]) || '';
  }

  static assign<T extends object>(target: T, ...sources: Partial<T>[]): T {
    return Object.assign(target, ...sources) as T;
  }
}

export interface IPlatformAnalyzer {
  getResult(): PlatformAnalysisResult;
}

export class PlatformAnalyzer implements IPlatformAnalyzer {
  private userAgent: string;
  private parsedResult: PlatformAnalysisResult;

  constructor() {
    this.userAgent = navigator.userAgent || '';
    KameleoonLogger.debug`PlatformParser: userAgent = ${this.userAgent}`;
    this.parsedResult = {};
    if (this.userAgent !== '') {
      this.parse();
    }
  }

  private parseBrowser(): BrowserResult {
    const browserRules = [
      {
        test: [/chrome|crios|crmo/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Chrome,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(
              /(?:chrome|crios|crmo)\/(\d+(\.?_?\d+)+)/i,
              userAgent,
            ),
          ),
        }),
      },
      {
        test: [/firefox|iceweasel|fxios/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Firefox,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(
              /(?:firefox|iceweasel|fxios)[\s/](\d+(\.?_?\d+)+)/i,
              userAgent,
            ),
          ),
        }),
      },
      {
        test: [/safari|applewebkit/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Safari,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/version\/(\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/opr\/|opios/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Opera,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/(?:opr|opios)[\s/](\S+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/edg([ea]|ios)/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.InternetExplorer,
          version: this.getBrowserVersion(
            Utils.getSecondMatch(/edg([ea]|ios)\/(\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/googlebot/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/googlebot\/(\d+(\.\d+))/i, userAgent) ||
              Utils.getFirstMatch(/version\/(\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/SamsungBrowser/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(
              /(?:SamsungBrowser)[\s/](\d+(\.?_?\d+)+)/i,
              userAgent,
            ),
          ),
        }),
      },
      {
        test: [/yabrowser/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(
              /(?:yabrowser)[\s/](\d+(\.?_?\d+)+)/i,
              userAgent,
            ),
          ),
        }),
      },
      {
        test: [/ucbrowser/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(
              /(?:ucbrowser)[\s/](\d+(\.?_?\d+)+)/i,
              userAgent,
            ),
          ),
        }),
      },
      {
        test: [/vivaldi/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/vivaldi\/(\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/phantom/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/phantomjs\/(\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/electron/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/(?:electron)\/(\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/chromium/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Other,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(
              /(?:chromium)[\s/](\d+(\.?_?\d+)+)/i,
              userAgent,
            ),
          ),
        }),
      },
      {
        test: [/opera/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.Opera,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/(?:opera)[\s/](\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/msie|trident/i],
        describe: (userAgent: string): BrowserResult => ({
          name: BrowserType.InternetExplorer,
          version: this.getBrowserVersion(
            Utils.getFirstMatch(/(?:msie |rv:)(\d+(\.?_?\d+)+)/i, userAgent),
          ),
        }),
      },
      {
        test: [/.*/i],
        describe: (userAgent: string): BrowserResult => {
          const fallbackRegex = userAgent.includes('(')
            ? /^(.*)\/(.*)[ \t]\((.*)/
            : /^(.*)\/(.*) /;
          return {
            name: BrowserType.Other,
            version: this.getBrowserVersion(
              Utils.getSecondMatch(fallbackRegex, userAgent),
            ),
          };
        },
      },
    ];

    const match = browserRules.find((rule) =>
      rule.test.some((regex) => regex.test(this.userAgent)),
    );

    if (match) {
      this.parsedResult.browser = match.describe(this.userAgent);
    }

    return this.parsedResult.browser || {};
  }

  private parseOS(): OSResult {
    const osRules = [
      {
        test: [/windows phone/i],
        describe: (): OSResult => ({ name: OperatingSystemType.WindowsPhone }),
      },
      {
        test: [/windows /i],
        describe: (): OSResult => ({ name: OperatingSystemType.Windows }),
      },
      {
        test: [/Macintosh(.*?) FxiOS(.*?) Version\//],
        describe: (userAgent: string): OSResult => ({
          name: OperatingSystemType.IOS,
          version: Utils.getSecondMatch(/(Version\/)(\d[\d.]+)/, userAgent),
        }),
      },
      {
        test: [/macintosh/i],
        describe: (userAgent: string): OSResult => ({
          name: OperatingSystemType.Mac,
          version: Utils.getFirstMatch(
            /mac os x (\d+(\.?_?\d+)+)/i,
            userAgent,
          ).replace(/[_\s]/g, '.'),
        }),
      },
      {
        test: [/(ipod|iphone|ipad)/i],
        describe: (userAgent: string): OSResult => ({
          name: OperatingSystemType.IOS,
          version: Utils.getFirstMatch(
            /os (\d+([_\s]\d+)*) like mac os x/i,
            userAgent,
          ).replace(/[_\s]/g, '.'),
        }),
      },
      {
        test: [/android/i],
        describe: (): OSResult => ({ name: OperatingSystemType.Android }),
      },
      {
        test: [/linux/i],
        describe: (): OSResult => ({ name: OperatingSystemType.Linux }),
      },
    ];

    const match = osRules.find((rule) =>
      rule.test.some((regex) => regex.test(this.userAgent)),
    );

    if (match) {
      this.parsedResult.os = match.describe(this.userAgent);
    }

    return this.parsedResult.os || {};
  }

  private parsePlatform(): PlatformResult {
    const platformRules = [
      {
        test: [/huserAgentwei/i],
        describe: (userAgent: string): PlatformResult => ({
          type: DeviceType.Phone,
        }),
      },
      {
        test: [/nexus\s*(?:7|8|9|10).*/i],
        describe: (): PlatformResult => ({ type: DeviceType.Tablet }),
      },
      {
        test: [/ipad/i],
        describe: (): PlatformResult => ({ type: DeviceType.Tablet }),
      },
      {
        test: [/Macintosh(.*?) FxiOS(.*?) Version\//],
        describe: (): PlatformResult => ({ type: DeviceType.Tablet }),
      },
      {
        test: [/kftt build/i],
        describe: (): PlatformResult => ({ type: DeviceType.Tablet }),
      },
      {
        test: [/silk/i],
        describe: (): PlatformResult => ({ type: DeviceType.Tablet }),
      },
      {
        test: [/tablet(?! pc)/i],
        describe: (): PlatformResult => ({ type: DeviceType.Tablet }),
      },
      {
        test: [/ipod|iphone/i],
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: [/nexus\s*[0-6].*/i, /galaxy nexus/i],
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: [/[^-]mobi/i],
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: [/blackberry/i],
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: [/bada/i],
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: [/windows phone/i],
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: [/android/i],
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: [/ios/i],
        describe: (): PlatformResult => ({ type: DeviceType.Desktop }),
      },
      {
        test: [/windows/i],
        describe: (): PlatformResult => ({ type: DeviceType.Desktop }),
      },
      {
        test: [/linux/i],
        describe: (): PlatformResult => ({ type: DeviceType.Desktop }),
      },
    ];
    const platformRulesOSName = [
      {
        test: () =>
          this.parsedResult.os?.name === OperatingSystemType.Android &&
          Number(String(this.parsedResult.os?.version).split('.')[0]) >= 3,
        describe: (userAgent: string): PlatformResult => ({
          type: DeviceType.Tablet,
        }),
      },
      {
        test: () => this.parsedResult.os?.name === OperatingSystemType.Android,
        describe: (): PlatformResult => ({ type: DeviceType.Phone }),
      },
      {
        test: () => this.parsedResult.os?.name === OperatingSystemType.Mac,
        describe: (): PlatformResult => ({ type: DeviceType.Desktop }),
      },
      {
        test: () => this.parsedResult.os?.name === OperatingSystemType.Windows,
        describe: (): PlatformResult => ({ type: DeviceType.Desktop }),
      },
      {
        test: () => this.parsedResult.os?.name === OperatingSystemType.Linux,
        describe: (): PlatformResult => ({ type: DeviceType.Desktop }),
      },
    ];

    const match =
      platformRules.find((rule) =>
        rule.test.some((regex) => regex.test(this.userAgent)),
      ) || platformRulesOSName.find((rule) => rule.test());

    if (match) {
      this.parsedResult.platform = match.describe(this.userAgent);
    }

    return this.parsedResult.platform || {};
  }

  private parse(): void {
    if (this.userAgent) {
      this.parseBrowser();
      this.parseOS();
      this.parsePlatform();
    }
  }

  private getBrowserVersion(version: string | undefined): number | undefined {
    if (!version) {
      return undefined;
    }

    const parts = version.split('.');
    if (parts.length === 0) {
      return undefined;
    }

    const majorMinor = parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
    const result = parseFloat(majorMinor);
    return isNaN(result) ? undefined : result;
  }

  public parseWebView(): void {
    let href = window.location?.href;
    if (href) {
      const title = document ? document.title : '';
      this.parsedResult.pageView = new PageView({
        urlAddress: href,
        title: title,
      });
    }
  }

  public getResult(): PlatformAnalysisResult {
    this.parseWebView();
    return Utils.assign({}, this.parsedResult);
  }
}
